<template>
  <form-layout title="Edit Details" @on-dismiss="tryDismiss">
    <vs-wrapper>
      <update-banner
        v-if="shouldShowUpdateCustomFieldsBanner"
        class="mb-2"
        @update-schema-requested="updateSchemaButtonClicked"
      />

      <vs-form ref="detailsEditForm">
        <vs-text-input v-model="details" label="Additional Details..." multiline />

        <cost-center-field
          :value="costCenter"
          :ticket-owner-id="job.ownerId"
          :ticket-destination="destinationLocationId"
          :ticket-sources="pickupLocationIds"
          :hub-id="jobHubId"
          label="Cost center"
          @input="updateCostCenter"
        />

        <schema-field
          v-if="!!schema.schema?.properties"
          :error-schema="errorSchema"
          :form-data="customData"
          :business-unit-id="jobHubId"
          :params="{ headingType: 'heading' }"
          :root-schema="schema.schema"
          :schema="schema.schema"
          :ui-schema="schema.uiSchema"
          :form-resolver="contextualizedFormResolver"
          @change="updateCustomData"
        />
      </vs-form>

      <v-spacer />

      <v-row no-gutters>
        <vs-button
          label="Save"
          :loading="saving"
          :disabled="!hasChanges"
          full-width
          @click="save"
        />
      </v-row>
    </vs-wrapper>

    <template #snack-bar-wrapper>
      <vs-snackbar v-model="isSnackbarVisible" type="error" sticky>
        Failed to update job details
      </vs-snackbar>
    </template>
  </form-layout>
</template>

<script setup lang="ts">
import { isEmpty, isEqual } from 'lodash'
import { computed, onMounted, ref } from 'vue'
import { useStore } from 'vuex-composition-helpers'

import { useCurrentUser } from '@/common/useCurrentUser'
import { BusinessUnit } from '@/models/business-unit'
import Job from '@/models/job'
import { CustomData } from '@/models/types/custom-data'
import { CustomFieldsSchema } from '@/models/types/custom-fields-schema'
import User from '@/models/user'
import { prepareCustomDataForSubmission } from '@/utils/field-schema-helper'
import { ContextualizedFormResolver } from '@/utils/forms/contextualized-form-resolver'
import { FormContext } from '@/utils/forms/form-context'
import { resolveFormDataForValidation } from '@/utils/forms/form-resolver'
import { validateFormData } from '@/utils/forms/form-validate'

import CostCenterField from '@/components/common/CostCenterField.vue'
import FormLayout from '@/components/common/FormLayout.vue'
import VsButton from '@/components/vision/VsButton.vue'
import VsForm from '@/components/vision/VsForm.vue'
import VsTextInput from '@/components/vision/Inputs/VsTextInput.vue'
import VsWrapper from '@/components/vision/VsWrapper.vue'

import UpdateBanner from './CustomFields/UpdateBanner.vue'
import { useJobBottomSheetStatesStore } from '@/components/jobs/jobBottomSheetStatesStore'
import { JobStopType } from '@ironsight/types'
import { getStopLocationId, getStopLocationIds } from './jobStopLocations'

interface Props {
  job: Job
  jobHubId: string
  schema: CustomFieldsSchema
  hasNewerCustomFieldsSchema: boolean
}

const props = defineProps<Props>()

const emit = defineEmits<{
  (e: 'details-updated')
  (e: 'update-schema-button-clicked')
}>()

const pickupLocationIds = computed(() =>
  getStopLocationIds(props.job.requestedStops, JobStopType.PickUp),
)
const destinationLocationId = computed(() =>
  getStopLocationId(props.job.requestedStops, JobStopType.DropOff),
)

const details = ref<string>('')
const costCenter = ref<string>('')
const customData = ref<CustomData>({})
const saving = ref<boolean>(false)
const isSnackbarVisible = ref<boolean>(false)
const errorSchema = ref<object>({})

const detailsEditForm = ref<typeof VsForm>()

const store = useStore()
const currentUser = useCurrentUser()

const ticketsError = computed<any>(() => store.getters['tickets/error'])
const getUserBySid = computed<(userSid: string) => User | undefined>(
  () => store.getters['users/getBySid'],
)
const getBusinessUnitById = computed<(id: string) => BusinessUnit | undefined | null>(
  () => store.getters['businessUnits/getBusinessUnitById'],
)

const hasChanges = computed<boolean>(() => {
  return (
    // Only eval the 1st grouping if versioned custom fields schema flag is on
    (props.schema.id && props.schema.id !== props.job.extensionSchemaId) ||
    details.value !== props.job.details ||
    costCenter.value !== props.job.costCenter ||
    !isEqual(customData.value, props.job.customData)
  )
})

const shouldShowUpdateCustomFieldsBanner = computed<boolean>(() => {
  return (
    props.hasNewerCustomFieldsSchema &&
    props.job.isArchived === false &&
    props.job.extensionSchemaId !== null
  )
})

const contextualizedFormResolver = computed<ContextualizedFormResolver>(() => {
  const hub = getBusinessUnitById.value(props.jobHubId)
  const user = getUserBySid.value(currentUser.value.userSid)

  const context = new FormContext({
    hub,
    user,
    additionalContext: {},
  })
  return new ContextualizedFormResolver(context)
})

onMounted(() => {
  details.value = props.job.details
  costCenter.value = props.job.costCenter
  customData.value = props.job.customData
})

function updateCostCenter(value: string | null) {
  costCenter.value = value ?? ''
}

function updateCustomData(value: CustomData) {
  customData.value = value
}

function updateSchemaButtonClicked() {
  emit('update-schema-button-clicked')
}

async function updateJob(job: Job) {
  await store.dispatch('jobs/updateJob', job)
}

function isValid() {
  // Depending on your IDE settings you may see warning here for calling `isValid`.
  // It is a false positive as the VsForm is not composition API yet
  return validateCustomFields() && detailsEditForm.value?.isValid()
}

function validateCustomFields() {
  const resolvedSchema = contextualizedFormResolver.value.retrieveSchema(
    props.schema.schema,
    props.schema.schema,
    customData.value,
  )
  const resolvedData = resolveFormDataForValidation(customData.value)

  const errors = validateFormData(resolvedSchema, resolvedData)
  errorSchema.value = errors

  return isEmpty(errors)
}

async function save() {
  if (!isValid()) return

  customData.value = prepareCustomDataForSubmission(props.schema, customData.value)

  const updatedJob = Object.assign({}, props.job, {
    details: details.value,
    costCenter: costCenter.value,
    customData: customData.value,
    extensionSchemaId: props.schema.id!, // always forward-fill
  })

  saving.value = true
  await updateJob(updatedJob)
  saving.value = false

  if (ticketsError.value) {
    isSnackbarVisible.value = true
  } else {
    emit('details-updated')
    tryDismiss(true)
  }
}

/**
 * Dismisses when forced to and when there are no unsaved changes.
 * @param force Force the dismissal
 */
function tryDismiss(force = false) {
  let isSafeToDismiss = force

  if (isSafeToDismiss === false && hasChanges.value === true) {
    isSafeToDismiss = window.confirm(
      'There are unsaved changes, are you sure you want to discard them?',
    )
  }

  if (isSafeToDismiss || hasChanges.value === false) {
    dismiss()
  }
}

const jobBottomSheetStatesStore = useJobBottomSheetStatesStore()

const dismiss = () => {
  jobBottomSheetStatesStore.setJobBottomSheetStates({
    isEditingJobDetailsBottomSheetVisible: false,
  })
}
</script>
